import { performanceApi } from "./apiClient.tsx";
import { handleApiError } from "./errorHandler.tsx";

import { CustomFormData } from "../modals/CustomProjectModal/CustomProjectModal.types";
import { StandardFormData } from "../modals/StandardProjectModal/StandardProjectModal.types";

/**
 * Lo que esta pantalla llama "proyecto" es una hoja de performance, y ahora eso es literal: una
 * hoja se crea sola y no arrastra nada más.
 *
 * Antes eran dos cosas —un proyecto en workflow y su cálculo en performance— y esta capa mantenía
 * la ilusión de una: creaba las dos, guardaba un mapa uuid de proyecto → uuid de cálculo, y
 * compensaba a mano cuando el segundo paso fallaba y dejaba el primero a medias. Nada de eso hace
 * falta ya. El uuid que ve la vista **es** el del cálculo.
 *
 * Esta pantalla no habla con workflow. Crear proyectos es de la otra UI, que es donde un proyecto
 * significa algo: un artefacto del pipeline comercial, no una cotización de las diez que se hacen
 * para quedarse con una.
 */

/** Crea la hoja. Un servicio, una llamada, un botón. */
const create = async (formData: StandardFormData | CustomFormData, custom: boolean) => {
  const path = custom ? "/calculations/custom" : "/calculations";
  const { data } = await performanceApi.post(path, formData);

  return data;
};

/**
 * Una hoja sin adoptar **omite** `project_id`, no lo manda `null`: la Api serializa con
 * `JsonIgnoreCondition.WhenWritingNull`, así que la clave no aparece. `project_id === null` es
 * siempre false y la etiqueta mentiría en todas las filas.
 */
export const isDraft = (calculation: { project_id?: string | null }): boolean =>
  !calculation.project_id;

/**
 * Todas las hojas del usuario. Con `unassignedOnly`, solo las que ningún proyecto ha adoptado —
 * el filtro lo resuelve el servidor, que ya lo sirve.
 */
export const getAllProjects = async (unassignedOnly = false): Promise<any> => {
  try {
    const { data } = await performanceApi.get("/calculations", {
      params: unassignedOnly ? { unassigned: true } : undefined,
    });
    return data;
  } catch (error: any) {
    throw handleApiError(error, {
      401: "Invalid or expired token",
    });
  }
};

export const getProject = async (uuid: string): Promise<any> => {
  try {
    const { data } = await performanceApi.get(`/calculations/${uuid}`);
    return data;
  } catch (error: any) {
    throw handleApiError(error, {
      401: "Invalid or expired token",
      404: "The project not found",
    });
  }
};

/**
 * El PDF ya no llega como una URL pública sino como el fichero, que es el arreglo: antes cualquiera
 * con el enlace —o adivinando un uuid— leía el reporte de otro cliente. Se devuelve una URL de
 * objeto para que quien llama la abra igual que antes; **hay que revocarla** al terminar.
 */
export const getPdf = async (uuid: string): Promise<string> => {
  try {
    const { data } = await performanceApi.get(`/calculations/${uuid}/performance`, {
      responseType: "blob",
    });

    return URL.createObjectURL(data);
  } catch (error: any) {
    throw handleApiError(error, {
      400: "The request body is invalid or improperly formatted",
      401: "Invalid or expired token",
      404: "PDF file not found",
    });
  }
};

export const createProject = async (formData: StandardFormData): Promise<any> => {
  try {
    return await create(formData, false);
  } catch (error: any) {
    throw handleApiError(error, {
      401: "Invalid or expired token",
      422: "The request body is invalid or improperly formatted",
    });
  }
};

export const createCustomProject = async (formData: CustomFormData): Promise<any> => {
  try {
    return await create(formData, true);
  } catch (error: any) {
    throw handleApiError(error, {
      401: "Invalid or expired token",
      422: "The request body is invalid or improperly formatted",
    });
  }
};

export const deleteProject = async (uuid: string): Promise<any> => {
  try {
    const { data } = await performanceApi.delete(`/calculations/${uuid}`);
    return data;
  } catch (error: any) {
    throw handleApiError(error, {
      401: "Invalid or expired token",
      404: "Project not found",
    });
  }
};

/**
 * Editar ya no sobrescribe: crea una revisión. La anterior queda intacta para siempre, que es lo
 * que arregla el fallo de que un `PATCH` destruyera una hoja ya entregada.
 *
 * La revisión es una fila nueva con **otro uuid**. Antes daba igual porque la vista se identificaba
 * por el del proyecto, que no cambiaba; ahora el uuid nuevo es el que vale, y quien llama recarga
 * la lista justo después, así que no hay identidad vieja que quede colgando.
 */
const revise = async (formData: StandardFormData | CustomFormData, uuid: string) => {
  const { data } = await performanceApi.post(`/calculations/${uuid}/revisions`, formData);

  return data;
};

export const updateProject = async (formData: StandardFormData, uuid: string): Promise<any> => {
  try {
    return await revise(formData, uuid);
  } catch (error: any) {
    throw handleApiError(error, {
      401: "Invalid or expired token",
      404: "Project not found",
      422: "The request body is invalid or improperly formatted",
    });
  }
};

export const updateCustomProject = async (formData: CustomFormData, uuid: string): Promise<any> => {
  try {
    return await revise(formData, uuid);
  } catch (error: any) {
    throw handleApiError(error, {
      401: "Invalid or expired token",
      404: "Project not found",
      422: "The request body is invalid or improperly formatted",
    });
  }
};
